/**
 * Trash. Lists soft-trashed skills (/admin/trash) and offers two actions, both
 * `manage_trash`-gated: restore (recoverable → a light confirm) and permanent
 * delete (irreversible → type-to-confirm). Server response is truth: re-read the
 * trash list after each mutation.
 */

import { useState } from "react";
import { api, ApiError } from "../api/client";
import type { AccountContext, TrashedSkill } from "../api/types";
import { TrashedChip } from "../components/Chips";
import { Confirm } from "../components/Confirm";
import { can } from "../lib/capabilities";
import { useToast } from "../components/Toast";
import { useAsyncData } from "../lib/useAsyncData";

type Pending = { kind: "restore" | "delete"; skill: TrashedSkill } | null;

export function Trash({ ctx }: { ctx: AccountContext }) {
  const data = useAsyncData(() => api.trash(), []);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const canManage = can(ctx.capabilities, "manage_trash");

  async function run() {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === "restore") {
        await api.restore(pending.skill.skillId);
        toast.push(`Restored ${pending.skill.skillId}.`);
      } else {
        await api.hardDelete(pending.skill.skillId);
        toast.push(`Permanently deleted ${pending.skill.skillId}.`);
      }
      setPending(null);
      data.reload();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "The action failed.", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1>Trash</h1>
      <p className="screen-intro">Soft-trashed skills. Restore them to the library, or delete them for good.</p>

      {data.loading && <p className="muted">Loading trash…</p>}
      {data.error && (
        <div className="banner banner--error" role="alert">
          {data.error} <button className="btn--link" onClick={data.reload}>Retry</button>
        </div>
      )}

      {data.value && data.value.count === 0 && (
        <div className="banner banner--info">The trash is empty.</div>
      )}

      {data.value && data.value.count > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Skill</th>
              <th>Status</th>
              <th>Trashed by</th>
              <th>When</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {data.value.skills.map((s) => (
              <tr key={s.skillId}>
                <td className="mono">{s.skillId}</td>
                <td>
                  <TrashedChip />
                </td>
                <td className="mono">{s.trashedBy}</td>
                <td className="muted">{formatDate(s.trashedAt)}</td>
                <td className="row-actions">
                  {canManage && (
                    <>
                      <button className="btn btn--small" onClick={() => setPending({ kind: "restore", skill: s })}>
                        Restore
                      </button>
                      <button
                        className="btn btn--small btn--danger"
                        onClick={() => setPending({ kind: "delete", skill: s })}
                      >
                        Delete forever
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pending?.kind === "restore" && (
        <Confirm
          title="Restore skill?"
          body={
            <>
              Restore <code className="mono">{pending.skill.skillId}</code> to the active library?
            </>
          }
          confirmLabel="Restore"
          busy={busy}
          onConfirm={run}
          onCancel={() => setPending(null)}
        />
      )}
      {pending?.kind === "delete" && (
        <Confirm
          title="Permanently delete skill?"
          tone="danger"
          confirmPhrase={pending.skill.skillId}
          body={
            <>
              This permanently deletes <code className="mono">{pending.skill.skillId}</code>. It cannot
              be restored.
            </>
          }
          confirmLabel="Delete forever"
          busy={busy}
          onConfirm={run}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}
